/**
 * H5 前端权限工具：统一读取 CoreIdentityPermissions / CoreIdentityRoles / CoreIsBoss，
 * 实现 PermissionCatalog.isCovered() 的前端等价逻辑：
 *   1. 精确匹配权限码
 *   2. manage 通配符覆盖 read（如 identity.organization.manage → identity.organization.read）
 *   3. 角色码匹配（TENANT_ADMIN / SYSTEM_ADMIN 等）
 *
 * 所有 Vue 组件、路由守卫统一走这个工具，不再各自 JSON.parse + includes。
 */
import identityApi from "@/api/identity";

/** 从 localStorage 安全读取 JSON 数组，失败返回空数组 */
function safeGetJSON(key) {
  try {
    const v = localStorage.getItem(key);
    return v ? JSON.parse(v) : [];
  } catch (_) {
    return [];
  }
}

/** 获取当前用户的权限码集合 */
export function getPermissions() {
  const perms = safeGetJSON('CoreIdentityPermissions');
  return Array.isArray(perms) ? perms : [];
}

/** 获取当前用户的角色编码集合（纯 code 字符串） */
export function getRoleCodes() {
  const roles = safeGetJSON('CoreIdentityRoles');
  if (!Array.isArray(roles)) return [];
  return roles.map(r => r.code || (typeof r === 'string' ? r : '')).filter(Boolean);
}

/** 从角色对象提取 code 字段（兼容 roles 数组元素可能是对象或字符串） */
function getRoleCode(r) {
  if (typeof r === 'string') return r;
  if (r && typeof r === 'object') return r.code || '';
  return '';
}

/**
 * 检查当前用户是否拥有指定权限（任一即可）。
 * 规则：精确匹配 || manage 通配符 || 角色匹配
 *
 * @param  {...string} codes 权限码，如 'identity.role.read', 'identity.role.manage'
 * @returns {boolean}
 */
export function hasPerm(...codes) {
  if (!codes.length) return true; // 不传权限码 = 所有人可访问
  const perms = getPermissions();
  if (!perms.length) return false;

  for (const code of codes) {
    // 1. 精确匹配
    if (perms.includes(code)) return true;
    // 2. manage 通配符覆盖 read（如 identity.role.manage 覆盖 identity.role.read）
    const dot2 = code.lastIndexOf('.');
    if (dot2 > 0) {
      const manageCode = code.substring(0, dot2) + '.manage';
      if (perms.includes(manageCode)) return true;
    }
  }
  return false;
}

/**
 * 检查当前用户是否拥有指定角色（任一即可）。
 * @param  {...string} codes 角色编码，如 'TENANT_ADMIN', 'SYSTEM_ADMIN'
 * @returns {boolean}
 */
export function hasRole(...codes) {
  if (!codes.length) return true;
  const roleCodes = getRoleCodes();
  return codes.some(c => roleCodes.includes(c));
}

/**
 * 路由守卫用：当前用户是否可以访问声明了 permCode 的路由。
 * 支持 permCode 是字符串（单一）或数组（任一即可）。
 * 同时支持 roleCode 检查（如 TENANT_ADMIN）。
 *
 * @param {{permCode?: string|string[], roleCode?: string|string[]}} meta
 * @returns {boolean}
 */
export function canAccessRoute(meta) {
  if (!meta) return true;
  const { permCode, roleCode } = meta;

  // 没有声明权限要求 = 全员可访问
  if (!permCode && !roleCode) return true;

  // 权限码检查
  if (permCode) {
    const codes = Array.isArray(permCode) ? permCode : [permCode];
    if (hasPerm(...codes)) return true;
    // 没通过权限码检查，继续看角色检查
  }

  // 角色检查
  if (roleCode) {
    const roles = Array.isArray(roleCode) ? roleCode : [roleCode];
    if (hasRole(...roles)) return true;
  }

  return false;
}

/**
 * 判断是否能访问 Boss 驾驶舱（/boss/home，渲染 BossHomeOverview 组件）。
 *
 * 与后端 BossService.requireBoss() 严格对齐：**只有 positionFamily === GENERAL_MANAGER 才是 Boss**。
 * 权限码命中（如 boss.dashboard.read）或角色命中（TENANT_ADMIN）不单独作为 Boss 判定条件——
 * 普通员工即使被授了 boss.project.read 也不能进入 Boss 驾驶舱，否则 BossHome 组件会去调
 * /boss/home-overview 等 Boss 专属接口，后端 requireBoss() 校验 positionFamily 不匹配 → 403。
 *
 * 兜底不再读 localStorage（与 Efficiency.vue isBoss 同根因：同浏览器切换账号时残留脏读），
 * 只信 synchronize 刚写入的 CorePositionFamily。
 */
export function isBossDashboardAccessible() {
  return localStorage.getItem('CorePositionFamily') === 'GENERAL_MANAGER';
}

/**
 * 重新拉取当前会话的身份权限缓存（轻量上下文接口 → localStorage）。
 * 用于角色权限配置保存后立即生效（否则要等下次进首页 synchronize 才更新）。
 * 失败静默返回 null——下次进首页仍会自动同步，不影响调用方流程。
 */
export async function refreshIdentityCache() {
  try {
    const ctx = await identityApi.myContext();
    localStorage.setItem('CoreIdentityRoles', JSON.stringify(ctx.roles || []));
    localStorage.setItem('CoreIdentityPermissions', JSON.stringify([...(ctx.permissions || [])]));
    localStorage.setItem('CoreIdentityDataScopes', JSON.stringify([...(ctx.dataScopes || [])]));
    localStorage.setItem('CoreIdentityUser', localStorage.getItem('UserSysno') || '');
    // 通知 Index.vue 等监听方刷新（Tab 显隐等）
    window.dispatchEvent(new CustomEvent('identity-updated'));
    return ctx;
  } catch (_) {
    return null;
  }
}

/**
 * 权限缓存自愈引导：CoreIdentityPermissions 历来只在进首页（HomeEntry.synchronize）时写入，
 * 导致在任意非首页直达/刷新（如 #/my）时缓存为空，hasPerm 全部返回 false——
 * Tab 与功能入口全部隐藏。这里按用户维度做一次性补拉（me/context 轻量接口），
 * 路由守卫在首次导航前 await，保证所有组件挂载时缓存已就绪。
 * 同一用户在一次页面生命周期内只拉一次；切换用户（UserSysno 变化）强制重拉。
 */
let bootstrapPromise = null;
let bootstrapUser = null;

function hasLocalIdentity(user) {
  const owner = localStorage.getItem('CoreIdentityUser');
  if (owner && owner !== user) return false;
  const raw = localStorage.getItem('CoreIdentityPermissions');
  if (!raw) return false;
  try {
    return Array.isArray(JSON.parse(raw));
  } catch (e) {
    return false;
  }
}

export function ensureIdentityCache() {
  const user = localStorage.getItem('UserSysno');
  if (!user) return Promise.resolve(null);
  if (bootstrapPromise && bootstrapUser === user) return bootstrapPromise;
  bootstrapUser = user;
  const refresh = refreshIdentityCache();
  // 本地已有当前用户的权限时，导航先放行，身份在后台更新
  if (hasLocalIdentity(user)) {
    bootstrapPromise = refresh.catch(() => null);
    return Promise.resolve(null);
  }
  bootstrapPromise = Promise.race([
    refresh,
    new Promise(resolve => setTimeout(() => resolve(null), 5000)) // 兜底超时，不阻塞导航
  ]);
  return bootstrapPromise;
}



// WEBPACK FOOTER //
// ./src/utils/perm.js